/**
 * Field Agent Activity Detail — Phase 2D (2026-04-24)
 * /portal/field-agent/activities/:activity_id
 *
 * Agent-side view of a single submitted activity.
 * Shows status, payout-readiness, review outcome, and the dispute panel
 * (when review.state is approved/rejected or ownership_conflict=true).
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Loader2, ArrowLeft, MapPin, Clock, User, Camera } from "lucide-react";
import AgentRenewalBanner from "../../components/fieldAgent/AgentRenewalBanner";
import AgentDisputePanel from "../../components/fieldAgent/AgentDisputePanel";
import PayoutReadinessPill from "../../components/fieldAgent/PayoutReadinessPill";

const STATE_COLORS = {
  pending: "bg-amber-500",
  under_review: "bg-indigo-600",
  approved: "bg-emerald-600",
  rejected: "bg-rose-600",
  returned_to_agent: "bg-slate-500",
  escalated: "bg-fuchsia-600",
};

export default function FieldAgentActivityDetail() {
  const { activity_id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/field-activities/${activity_id}`);
      setActivity(res.data.activity);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not load activity");
    } finally { setLoading(false); }
  }, [activity_id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="field-agent-activity-detail-page">
      <div className="max-w-2xl mx-auto px-4 pt-3"><AgentRenewalBanner /></div>
      <div className="max-w-2xl mx-auto px-4 py-3">
        <div className="flex items-center gap-2 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/portal/field-agent/weekly-rollup")} data-testid="agent-activity-detail-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Rollup
          </Button>
          {activity?.review?.state && <Badge className={`${STATE_COLORS[activity.review.state] || "bg-slate-400"} text-white`} data-testid="agent-activity-state">{activity.review.state}</Badge>}
          <PayoutReadinessPill activity={activity} />
        </div>

        {error && <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm text-rose-700">{String(error)}</div>}

        {!error && activity && (
          <>
            <AgentDisputePanel activity={activity} onReload={load} />

            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-3">
              <div className="text-sm font-semibold mb-2">Activity {activity.id}</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-slate-500">Date</div><div className="font-medium">{activity.activity_date}</div></div>
                <div><div className="text-xs text-slate-500">Parish</div><div className="font-medium"><MapPin className="w-3 h-3 inline mr-1" />{activity.parish}</div></div>
                <div><div className="text-xs text-slate-500">Outcome</div><div className="font-medium">{activity.visit_outcome}</div></div>
                <div><div className="text-xs text-slate-500">Intent</div><div className="font-medium">{activity.conversion_intent}</div></div>
                <div className="col-span-2"><div className="text-xs text-slate-500">Prospect</div><div className="font-medium"><User className="w-3 h-3 inline mr-1" />{activity.prospect?.name || "—"} · {activity.prospect?.type}</div></div>
              </div>
              {activity.notes && <div className="mt-2 text-sm text-slate-700 italic">"{activity.notes}"</div>}
            </div>

            {activity.review?.state === "returned_to_agent" && activity.review?.flagged_fields?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 text-sm text-amber-900" data-testid="agent-returned-banner">
                <div className="font-semibold mb-1">Returned for changes</div>
                <div className="text-xs">Flagged fields: {activity.review.flagged_fields.join(", ")}</div>
                {activity.review.last_return_reason && <div className="italic mt-1 text-xs">"{activity.review.last_return_reason}"</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
